import { ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import AviumLogo from './AviumLogo';

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  // Optional footer area for a primary action (e.g. "Done" on the ticket
  // detail modal). When set, renders a separator + right-aligned action row.
  footer?: ReactNode;
  // Opt into the brand header: brand-primary-dark (#134E4A) bg + white title +
  // AviumLogo on the left + orange spark accent stripe. Used on the
  // ticket-detail (Edit) modal and any "this is an Avium surface" modal so
  // they speak the same visual language as the Create Ticket modal +
  // ResourceSidePanel.
  branded?: boolean;
  // Optional subtitle under the title (only rendered in branded mode).
  subtitle?: string;
}

// Focusable-element selector for the focus trap.
const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export default function Modal({ open, title, onClose, children, size = 'md', footer, branded = false, subtitle }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // Accessibility: when open, move focus into the dialog, trap Tab within it,
  // close on Escape, and restore focus to the opener on close. This makes the
  // 19 shared modals keyboard-safe and screen-reader-correct — an enterprise
  // procurement gate (WCAG) — without changing any caller's props.
  useEffect(() => {
    if (!open) return;
    openerRef.current = (document.activeElement as HTMLElement | null) ?? null;
    const panel = panelRef.current;
    const focusables = () => (panel ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)) : []);
    (focusables()[0] ?? panel)?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const f = focusables();
      if (f.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = f[0];
      const last = f[f.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      openerRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  // Portaled to <body>: `fixed` must resolve against the viewport, but an
  // ancestor with backdrop-filter/transform (e.g. PageChrome's blurred sticky
  // row hosting AskAviumLauncher) becomes the containing block for fixed
  // descendants — the overlay then anchors to that box and the scroll parent
  // clips it under the staging/demo banners.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Frosted backdrop (2026-07-01): the app stays visible but blurs and
          recedes, so the modal reads as a layer ON the app, not a replacement.
          Deep-teal tint keeps the dimmed state in the brand world. */}
      <div className="absolute inset-0 bg-brand-primary-deepest/45 backdrop-blur-md motion-safe:animate-fade-in" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`relative bg-card-raised rounded-xl shadow-pop motion-safe:animate-modal-in w-full ${widths[size]} mx-4 max-h-[90vh] flex flex-col overflow-hidden focus:outline-none`}
      >
        {branded ? (
          <>
            <div className="relative isolate bg-brand-primary-dark px-6 py-4 flex items-center gap-3 shrink-0">
              <div aria-hidden className="aurora-layer" />
              <AviumLogo size={36} variant="dark" className="shrink-0" />
              <div className="flex-1 min-w-0">
                <h2 id={titleId} className="text-base font-semibold text-white truncate">{title}</h2>
                {subtitle && <p className="text-xs text-white/70 mt-0.5 truncate">{subtitle}</p>}
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="text-white/70 hover:text-white text-xl leading-none shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-primary-dark rounded"
              >×</button>
            </div>
            <div className="h-1 bg-brand-spark shrink-0" />
          </>
        ) : (
          <div className="flex items-center justify-between px-6 py-4 border-b border-line">
            <h2 id={titleId} className="text-lg font-semibold text-ink">{title}</h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="inline-flex items-center justify-center w-8 h-8 rounded-full text-ink-faint hover:text-ink hover:bg-wash transition-colors text-2xl leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
            >×</button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && (
          <div className="px-6 py-3 border-t border-line bg-wash flex items-center justify-end gap-2 rounded-b-xl">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
